import { useTooltip } from "@nivo/tooltip";
import React from "react";

/**
 * Shared chart helpers (dashboard charts) — mirror the widget's chart polish:
 *  - `yTickValues`: reduce Y-axis clutter (fewer numbers + grid lines), integer
 *    ticks for whole-number data (counts).
 *  - `makeRoundedBar`: a custom Nivo bar renderer that rounds only the "value end"
 *    (top for vertical bars, right for horizontal) instead of all four corners.
 */

/**
 * Fewer, rounded Y-axis tick values. Returns integer ticks `[0..max]` for
 * whole-number, non-negative data (so a chart maxing at 3 shows 0/1/2/3, not
 * 0.2/0.4/…); otherwise returns the numeric target count for Nivo/d3 to pick
 * ~that many nice ticks within the auto domain.
 */
export const yTickValues = (
  values: number[],
  targetCount: number,
): number[] | number => {
  const nums = values.filter((v) => typeof v === "number" && isFinite(v));
  if (nums.length === 0) return targetCount;
  const max = Math.max(...nums);
  const min = Math.min(...nums);
  if (max <= 0 || min < 0) return targetCount;
  if (!nums.every((v) => Number.isInteger(v))) return targetCount;
  const rawStep = Math.max(1, max / targetCount);
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const niceStep = Math.max(
    1,
    Math.round((norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * mag),
  );
  const ticks: number[] = [];
  for (let v = 0; v <= max + 1e-9; v += niceStep) ticks.push(v);
  return ticks;
};

/** SVG path for a bar with only its "value end" rounded (top for vertical bars,
 *  right for horizontal), squared on the baseline side. */
const roundedBarPath = (
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
  layout: "vertical" | "horizontal",
): string => {
  const W = Math.max(0, w);
  const H = Math.max(0, h);
  if (W <= 0 || H <= 0) return "";
  if (layout === "horizontal") {
    const r = Math.max(0, Math.min(radius, H / 2, W));
    return `M${x},${y} L${x + W - r},${y} Q${x + W},${y} ${x + W},${y + r} L${
      x + W
    },${y + H - r} Q${x + W},${y + H} ${x + W - r},${y + H} L${x},${y + H} Z`;
  }
  const r = Math.max(0, Math.min(radius, W / 2, H));
  return `M${x},${y + H} L${x},${y + r} Q${x},${y} ${x + r},${y} L${
    x + W - r
  },${y} Q${x + W},${y} ${x + W},${y + r} L${x + W},${y + H} Z`;
};

const readSpring = (v: any): any =>
  v && typeof v.get === "function" ? v.get() : v;

/**
 * For a stacked bar, only the topmost segment of each category should be rounded.
 * A bar datum is the top segment when its key is the last key (in stack order,
 * keys[0] = baseline) with a non-zero value for its category. Non-stacked and
 * single-key charts always round.
 */
const isTopStackSegment = (
  barDatum: any,
  keys: string[],
  isStacked: boolean,
): boolean => {
  if (!isStacked || !keys || keys.length <= 1) return true;
  const row = barDatum?.data ?? {};
  for (let i = keys.length - 1; i >= 0; i--) {
    const v = row[keys[i]];
    if (v !== undefined && v !== null && v !== 0)
      return keys[i] === barDatum.id;
  }
  return true;
};

/**
 * Custom Nivo bar renderer with a rounded "value end". For stacked bars only the
 * topmost segment is rounded. Renders statically while reusing Nivo's `useTooltip`
 * so hover tooltips and value labels still work.
 */
export const makeRoundedBar =
  (
    radius: number,
    layout: "vertical" | "horizontal",
    labelTextStyle: Record<string, any>,
    keys: string[],
    isStacked: boolean,
  ): React.FC<any> =>
  ({
    bar,
    style,
    borderWidth,
    label,
    shouldRenderLabel,
    isInteractive,
    onClick,
    onMouseEnter,
    onMouseLeave,
    tooltip,
  }: any) => {
    const { x, y, width, height, color, data } = bar;
    const { showTooltipFromEvent, hideTooltip } = useTooltip();
    const fill = data?.fill ?? color;
    const effRadius = isTopStackSegment(data, keys, isStacked) ? radius : 0;
    const showTip = (e: React.MouseEvent) =>
      showTooltipFromEvent(
        React.createElement(tooltip, { ...bar, ...data }),
        e,
      );
    return (
      <g>
        <path
          d={roundedBarPath(x, y, width, height, effRadius, layout)}
          fill={fill}
          strokeWidth={borderWidth}
          stroke={readSpring(style?.borderColor)}
          onMouseEnter={
            isInteractive
              ? (e) => {
                  if (onMouseEnter) onMouseEnter(data, e);
                  showTip(e);
                }
              : undefined
          }
          onMouseMove={isInteractive ? showTip : undefined}
          onMouseLeave={
            isInteractive
              ? (e) => {
                  if (onMouseLeave) onMouseLeave(data, e);
                  hideTooltip();
                }
              : undefined
          }
          onClick={
            isInteractive
              ? (e) => onClick && onClick({ ...data, color }, e)
              : undefined
          }
        />
        {shouldRenderLabel && (
          <text
            x={x + width / 2}
            y={y + height / 2}
            textAnchor="middle"
            dominantBaseline="central"
            style={{
              ...labelTextStyle,
              fill: readSpring(style?.labelColor),
              pointerEvents: "none",
            }}
          >
            {label}
          </text>
        )}
      </g>
    );
  };
